import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { useDispatch, useSelector } from 'react-redux';
import {
    Table, TableBody, Typography, TableContainer, TableHead, TableRow, Paper,
    IconButton, Stack, Pagination, Box, Button, Chip, Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import { DeleteConfirmationModal } from '../../../../utils/modal';
import SearchInput from '../../../../utils/searchInput';
import {
    getTemplatesRequest,
    deleteTemplateRequest,
    resetDeleteTemplate,
    seedTemplatesRequest,
    resetSeedTemplates
} from '../../../../redux/modules/reportTemplate/slice';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const EllipsisText = ({ children }) => (
    <Typography noWrap sx={{ maxWidth: 200 }}>
        {children}
    </Typography>
);

const categoryColors = {
    OB: { bg: '#E91E63', color: '#fff' },
    GYN: { bg: '#9C27B0', color: '#fff' },
    Abdominal: { bg: '#2E7D32', color: '#fff' },
    Thyroid: { bg: '#1565C0', color: '#fff' },
    Renal: { bg: '#E65100', color: '#fff' },
    Vascular: { bg: '#C62828', color: '#fff' },
    Breast: { bg: '#6A1B9A', color: '#fff' },
    Cardiac: { bg: '#AD1457', color: '#fff' },
    Other: { bg: '#546E7A', color: '#fff' },
};

let deleteItemId;
let limit = 10;
let pageCount = 0;

export default function List({ setItemData, toggleList, currentPage, setCurrentPage }) {
    const dispatch = useDispatch();
    const { loading, templates, deleteRes, seedRes, seedLoading } = useSelector(state => state.reportTemplate);
    const { currentBranch } = useSelector(state => state.branch);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        if (!currentBranch) return;
        dispatch(getTemplatesRequest({ page: currentPage, limit, searchQuery }));
    }, [dispatch, currentBranch, currentPage, searchQuery]);

    useEffect(() => {
        if (!deleteRes) return;
        if (deleteRes?.success) {
            dispatch(getTemplatesRequest({ page: currentPage, limit }));
            toast.success(deleteRes?.message);
        } else {
            toast.error(deleteRes?.message);
        }
        dispatch(resetDeleteTemplate());
    }, [dispatch, deleteRes, currentPage]);

    useEffect(() => {
        if (!seedRes) return;
        if (seedRes?.success) {
            dispatch(getTemplatesRequest({ page: currentPage, limit }));
            toast.success(seedRes?.message);
        } else {
            toast.error(seedRes?.message);
        }
        dispatch(resetSeedTemplates());
    }, [dispatch, seedRes, currentPage]);

    // Calculate pagination
    const dataCount = templates?.length || 0;
    if (dataCount > limit) {
        pageCount = Math.ceil(dataCount / limit);
    }

    const handleSeedTemplates = () => {
        dispatch(seedTemplatesRequest());
    };

    return (
        <>
            <Stack mb={3} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                <SearchInput setSearchQuery={(searchValue) => { setSearchQuery(searchValue); setCurrentPage(1); }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Load default templates for all categories">
                        <Button
                            variant="outlined"
                            onClick={handleSeedTemplates}
                            startIcon={<RefreshIcon />}
                            disabled={seedLoading}
                        >
                            {seedLoading ? 'Loading...' : 'Load Defaults'}
                        </Button>
                    </Tooltip>
                    <Button
                        className='commonBtn'
                        variant="contained"
                        onClick={() => { toggleList(); setItemData({}); }}
                        startIcon={<AddIcon />}
                    >
                        Add Template
                    </Button>
                </Box>
            </Stack>
            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
                <TableContainer component={Paper} className='tableShadow'>
                    <Table sx={{ minWidth: { xs: 500, md: 700 } }} aria-label="templates table" className='commonTable'>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Name</StyledTableCell>
                                <StyledTableCell>Category</StyledTableCell>
                                <StyledTableCell>Description</StyledTableCell>
                                <StyledTableCell align="center">Normal</StyledTableCell>
                                <StyledTableCell align="right">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {templates?.length > 0 ?
                                templates
                                    .filter(t => !searchQuery || t.name?.toLowerCase().includes(searchQuery.toLowerCase()))
                                    .slice((currentPage - 1) * limit, currentPage * limit)
                                    .map(template => (
                                        <StyledTableRow key={template._id}>
                                            <StyledTableCell align="left">
                                                <EllipsisText>{template.name}</EllipsisText>
                                            </StyledTableCell>
                                            <StyledTableCell align="left">
                                                <Chip
                                                    label={template.category}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: (categoryColors[template.category] || categoryColors.Other).bg,
                                                        color: (categoryColors[template.category] || categoryColors.Other).color,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell align="left">
                                                <EllipsisText>{template.description || '-'}</EllipsisText>
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {template.isNormal && (
                                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <IconButton color="primary" onClick={() => { setItemData(template); toggleList(); }}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => { deleteItemId = template._id; setIsModalOpen(true); }}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                :
                                <StyledTableRow>
                                    <StyledTableCell colSpan={5} align="center">
                                        <Typography color="text.secondary">
                                            No templates found. Click "Load Defaults" to add standard templates.
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {(templates?.length > limit) && (
                <Pagination
                    count={pageCount}
                    page={currentPage}
                    onChange={(event, value) => { setCurrentPage(value); }}
                    sx={{ mt: 2 }}
                />
            )}
            <DeleteConfirmationModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={() => {
                    dispatch(deleteTemplateRequest({ id: deleteItemId }));
                    setIsModalOpen(false);
                }}
            />
        </>
    );
}
